import { ColumnState } from 'ag-grid-community';
import React, { useState, useEffect, useMemo } from 'react';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import useGetMacroAssortment from '@store/hooks/useGetMacroAssortment';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import { AgileMacroAssortment } from '@components/agile/types/AgileMacroAssortmentTypes';
import ImageCellRenderer from '@components/agile/agileComponents/agGridCommonComponents/ImageCellRenderer';
import getDateInUTC from '@components/agile/utils/GetDateUtils';
import { INDEFINITE_TIME } from '@constants/strings';
import ColumnSelector from '@components/shared/ColumnSelector';
import '../ZoneMap.css';
import { useColumnSelector } from 'src/hooks/useColumnSelector';

export interface AtAssortmentSearchTableProps {
  zoneDetails: AgileMacroplanogram | undefined;
  swapTime: number;
  includeFuture: boolean;
  includeDraft: boolean;
}

const AtAssortmentSearchTable = (props: AtAssortmentSearchTableProps) => {
  const [rowData, setRowData] = useState<AgileMacroAssortment[]>([]);
  const { zoneDetails, swapTime, includeFuture, includeDraft } = props;
  const allColumns = [
    { headerName: 'ASIN', field: 'asin', editable: false, pinned: true },
    {
      headerName: 'Title',
      field: 'title',
      editable: false,
      pinned: true,
    },
    {
      field: 'image',
      editable: false,
      cellRenderer: ImageCellRenderer,
      pinned: true,
    },
    { field: 'facingUnits', editable: false },
    {
      headerName: 'Total FOH Capacity (Units)',
      wrapHeaderText: true,
      headerClass: 'text-center',
      field: 'fohCapacity',
      editable: false,
    },
    {
      headerName: 'UPHL1',
      field: 'uphl1',
      editable: false,
    },
    {
      headerName: 'UPHL2',
      field: 'uphl2',
      editable: false,
    },
    {
      headerName: 'UPHL3',
      field: 'uphl3',
      editable: false,
    },
    { headerName: 'POG Live Date', field: 'startTime', editable: false, pinned: true },
    { headerName: 'POG End Date', field: 'endTime', editable: false },
    { headerName: 'Shrink Cost (-1W)', field: 'shrinkCost', editable: false },
    { headerName: 'Shrink % Ops (-1W) %', field: 'shrinkPerOps', editable: false, wrapHeaderText: true },
    {
      headerName: 'Shrink % Ops (-13W) %',
      field: 'shrinkPerOps13week',
      editable: false,
      minWidth: 160,
      wrapHeaderText: true,
    },
    {
      headerName: 'Shrink % Ops (-26W) %',
      field: 'shrinkPerOps26week',
      editable: false,
      minWidth: 160,
      wrapHeaderText: true,
    },
    {
      headerName: 'Shrink % Ops (-52W) %',
      field: 'shrinkPerOps52week',
      editable: false,
      minWidth: 160,
      wrapHeaderText: true,
    },
    {
      headerName: 'Moving Avg Sales (-13W) $',
      field: 'movingAvgSales13week',
      editable: false,
      minWidth: 160,
      wrapHeaderText: true,
    },
    {
      headerName: 'Moving Avg Sales (-26W) $',
      field: 'movingAvgSales26week',
      editable: false,
      minWidth: 160,
      wrapHeaderText: true,
    },
    {
      headerName: 'Moving Avg Sales (-52W) $',
      field: 'movingAvgSales52week',
      editable: false,
      minWidth: 160,
      wrapHeaderText: true,
    },
  ];

  const defaultVisibleColumns = [
    'asin',
    'title',
    'image',
    'facingUnits',
    'fohCapacity',
    'startTime',
    'shrinkPerOps',
    'shrinkPerOps13week',
    'shrinkPerOps26week',
    'movingAvgSales13week',
    'movingAvgSales52week',
  ];

  const { visibleColumns, columnDefs, toggleColumn } = useColumnSelector({
    allColumns,
    defaultVisibleColumns,
    storageKey: 'assortmentSearchColumns',
  });

  const defaultColDef = useMemo(
    () => ({
      editable: true,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      sortable: true,
      resizable: true,
      filter: true,
      flex: 1,
      minWidth: 100,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    }),
    [],
  );

  const { macroAssortmentResult, hasNextPage, isLoading, fetchNextPage } = useGetMacroAssortment({
    storeId: zoneDetails?.storeId ? zoneDetails?.storeId : '',
    macroplanogramId: zoneDetails?.entryId ? zoneDetails?.entryId : '',
    timestamp: swapTime,
    includeFuture,
    maxResult: 100,
    includeDraft,
  });
  useEffect(() => {
    const tempRowData: AgileMacroAssortment[] = [];
    const assortmentResult = macroAssortmentResult || [];
    assortmentResult.forEach((rawRow) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const endTime = rawRow.endTime >= INDEFINITE_TIME ? 'Indefinite' : getDateInUTC(rawRow.endTime);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const startTime = getDateInUTC(rawRow.startTime);
      const rowObj = {
        asin: rawRow.asin,
        facingUnits: +rawRow.facingUnits,
        startTime,
        endTime,
        fohCapacity: Number(rawRow.fohCapacity),
        title: rawRow.asinMetadata?.title ?? 'Unknown',
        image: rawRow.asinMetadata?.image,
        uphl1: rawRow.asinMetadata?.uph?.uphl1 ?? 'Unknown',
        uphl2: rawRow.asinMetadata?.uph?.uphl2 ?? 'Unknown',
        uphl3: rawRow.asinMetadata?.uph?.uphl3 ?? 'Unknown',
        shrinkCost: rawRow.asinMetadata?.performanceData?.shrinkCost ?? 'N/A',
        shrinkPerOps: rawRow.asinMetadata?.performanceData?.shrinkPerOps ?? 'N/A',
        shrinkPerOps13week: rawRow.asinMetadata?.performanceData?.shrinkPerOps13week ?? 'N/A',
        shrinkPerOps26week: rawRow.asinMetadata?.performanceData?.shrinkPerOps26week ?? 'N/A',
        shrinkPerOps52week: rawRow.asinMetadata?.performanceData?.shrinkPerOps52week ?? 'N/A',
        movingAvgSales13week: rawRow.asinMetadata?.performanceData?.movingAvgSales13week ?? 'N/A',
        movingAvgSales26week: rawRow.asinMetadata?.performanceData?.movingAvgSales26week ?? 'N/A',
        movingAvgSales52week: rawRow.asinMetadata?.performanceData?.movingAvgSales52week ?? 'N/A',
      } as AgileMacroAssortment;
      tempRowData.push(rowObj);
    });
    setRowData(tempRowData);
  }, [macroAssortmentResult]);

  return isLoading ? (
    <Column alignmentHorizontal="center" alignmentVertical="center" spacing="600" spacingInset="500">
      <Loader type="circular" size="large" />{' '}
    </Column>
  ) : (
    <div className="ag-theme-alpine">
      <Row alignmentHorizontal="end" spacingInset="200">
        <ColumnSelector allColumns={allColumns} visibleColumns={visibleColumns} onToggleColumn={toggleColumn} />
      </Row>
      <AgGridReact<AgileMacroAssortment>
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowSelection="multiple"
        suppressRowClickSelection
        domLayout="autoHeight"
        enableCellTextSelection
        onGridReady={(params) => {
          params.api.sizeColumnsToFit();
          const columnState: ColumnState[] = [
            {
              colId: 'startTime',
              sort: 'desc',
            },
          ];
          params.columnApi.applyColumnState({
            state: columnState,
            applyOrder: true,
          });
        }}
      />
      <Row alignmentHorizontal="center" spacingInset="400">
        {isLoading && <Loader type="circular" size="large" />}
        {hasNextPage && (
          <Button
            onClick={async () => {
              await fetchNextPage();
            }}
            type="primary"
          >
            Load More
          </Button>
        )}
      </Row>
    </div>
  );
};
export default AtAssortmentSearchTable;
